import { KRP_TESTIMONIALS } from "../../../mocks/krp-data";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface TestimonialsContent {
  badge?: string;
  headline: string;
  subtext: string;
}

interface TestimonialsSectionProps {
  content: TestimonialsContent;
}

export default function TestimonialsSection({ content }: TestimonialsSectionProps) {
  const { badge, headline, subtext } = content;
  const headerRef = useScrollRevealChildren();
  const featuredRef = useScrollRevealChildren();
  const listRef = useScrollRevealChildren();

  return (
    <section className="bg-[#F5F0E8] py-28 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 xl:px-10">

        {/* Header */}
        <div ref={headerRef} className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-8">
          <div className="max-w-2xl">
            {badge && (
              <div className="reveal flex items-center gap-4 mb-6">
                <div className="h-px w-12 bg-[#C9A961]" />
                <span
                  className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {badge}
                </span>
              </div>
            )}
            <h2
              className="reveal delay-100 text-4xl md:text-5xl xl:text-[58px] text-[#1C1C1C] leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
            >
              {headline}
            </h2>
          </div>
          <div className="reveal delay-200 flex items-center gap-3 flex-shrink-0">
            <div>
              <div className="flex gap-1 mb-1">
                {[1,2,3,4,5].map((s) => (
                  <i key={s} className="ri-star-fill text-[#C9A961] text-base" />
                ))}
              </div>
              <p
                className="text-[#444] text-sm font-semibold"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                4.9 / 5.0 · 150+ Reviews
              </p>
              <p
                className="text-[#AAA] text-[11px]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {subtext}
              </p>
            </div>
          </div>
        </div>

        {/* Cinematic featured review */}
        <div ref={featuredRef} className="reveal-scale mb-3">
          <div className="relative overflow-hidden">
            {/* Background image */}
            <div
              className="absolute inset-0 bg-cover bg-center scale-105"
              style={{
                backgroundImage:
                  "url(https://public.readdy.ai/ai/img_res/48cbed6cbf61fefc399eacf87433c3c0.jpg)",
              }}
            />
            <div className="absolute inset-0 bg-[#0A0A0A]/94" />

            {/* Decorative quote mark */}
            <div
              className="absolute -top-4 right-8 text-[#C9A961]/5 text-[280px] leading-none font-serif select-none"
              style={{ fontFamily: "'Playfair Display', serif" }}
              aria-hidden="true"
            >
              &ldquo;
            </div>

            <div className="relative z-10 px-8 md:px-16 xl:px-20 py-16 md:py-20">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">

                {/* Left: Stats */}
                <div className="lg:col-span-3 flex lg:flex-col gap-10 lg:gap-8">
                  <div>
                    <div
                      className="text-[70px] font-bold text-[#C9A961] leading-none"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      4.9
                    </div>
                    <div className="flex gap-0.5 my-1">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className="ri-star-fill text-[#C9A961] text-xs" />
                      ))}
                    </div>
                    <div
                      className="text-white/35 text-[9px] tracking-[0.22em] uppercase"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Average Rating
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[44px] font-bold text-white leading-none"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      150+
                    </div>
                    <div
                      className="text-white/35 text-[9px] tracking-[0.22em] uppercase mt-1"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Verified Reviews
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="hidden lg:flex lg:col-span-1 justify-center">
                  <div className="w-px h-48 bg-gradient-to-b from-transparent via-white/10 to-transparent" />
                </div>

                {/* Right: Quote */}
                <div className="lg:col-span-8">
                  <blockquote
                    className="text-white text-2xl md:text-3xl xl:text-4xl leading-[1.4] mb-10"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
                  >
                    &ldquo;{KRP_TESTIMONIALS[0].text}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-5">
                    <div className="w-14 h-14 overflow-hidden flex-shrink-0 border border-[#C9A961]/30">
                      <img
                        src={KRP_TESTIMONIALS[0].avatar}
                        alt={KRP_TESTIMONIALS[0].name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div>
                      <p
                        className="text-white font-semibold text-sm"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {KRP_TESTIMONIALS[0].name}
                      </p>
                      <p
                        className="text-[#C9A961] text-xs mt-0.5"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {KRP_TESTIMONIALS[0].location}
                      </p>
                      <p
                        className="text-white/25 text-[11px] mt-0.5"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {KRP_TESTIMONIALS[0].project}
                      </p>
                    </div>
                    <div className="ml-auto hidden sm:block">
                      <div className="border border-[#C9A961]/20 px-4 py-2">
                        <span
                          className="text-[#C9A961]/60 text-[9px] tracking-[0.2em] uppercase"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          Verified Client
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Supporting testimonials — editorial horizontal strips */}
        <div ref={listRef} className="space-y-px">
          {KRP_TESTIMONIALS.slice(1).map((testimonial, idx) => (
            <div
              key={testimonial.id}
              className={`reveal delay-${idx * 80 + 100} group grid grid-cols-12 gap-6 items-center bg-white hover:bg-white px-8 py-7 border-l-2 border-transparent hover:border-[#C9A961] transition-all duration-400`}
            >
              {/* Avatar */}
              <div className="col-span-1 hidden md:flex justify-center">
                <div className="w-12 h-12 overflow-hidden flex-shrink-0 border border-[#F0EBE2] group-hover:border-[#C9A961]/30 transition-colors">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Name + Location */}
              <div className="col-span-12 md:col-span-2">
                <p
                  className="text-[#1C1C1C] font-semibold text-sm"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {testimonial.name}
                </p>
                <p
                  className="text-[#C9A961] text-[11px] mt-0.5"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {testimonial.location}
                </p>
                <div className="flex gap-0.5 mt-1.5">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-[#C9A961] text-[9px]" />
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:flex col-span-1 justify-center">
                <div className="w-px h-10 bg-[#F0EBE2] group-hover:bg-[#C9A961]/20 transition-colors" />
              </div>

              {/* Quote */}
              <div className="col-span-12 md:col-span-7">
                <blockquote
                  className="text-[#555] leading-relaxed text-[15px]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
                >
                  &ldquo;{testimonial.text}&rdquo;
                </blockquote>
              </div>

              {/* Project tag */}
              <div className="hidden lg:flex col-span-1 justify-end">
                <span
                  className="text-[#C9A961]/50 text-[9px] tracking-[0.15em] uppercase whitespace-nowrap group-hover:text-[#C9A961] transition-colors"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {testimonial.project}
                </span>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
